// src/components/workflow/WorkflowStatusProgress.tsx
import React from 'react';
import { Check, XCircle, CheckCircle, Loader2 } from 'lucide-react';

interface WorkflowStep {
  step_order: number;
  step_label: string | null;
  can_resolve_here: boolean;
  validator: { id: number; name: string } | null;
  validator_role: string | null;
  status: string;
  feedback: string | null;
  action_at: string | null;
}

interface Props {
  steps: WorkflowStep[];
  currentStep: number | null;
  resolution: string | null;
  needsRapim: boolean | null;
  laporanStatus: string;
}

// ✅ STATUS YANG DIANGGAP "AKTIF PENOLAKAN"
const REJECTED_STATUSES = ['ditolak_l1', 'ditolak_l2'];

// ✅ STATUS YANG DIANGGAP "AKTIF SEDANG PROSES"
const ACTIVE_PROCESS_STATUSES = [
  'diajukan',
  'validasi_l1',
  'validasi_l2',
  'antrean_rapim',
  'diputuskan',
  'berlangsung',
  'selesai',
  'diarsipkan',
  'diselesaikan_lokal',
];

const WorkflowStatusProgress: React.FC<Props> = ({
  steps,
  currentStep,
  resolution,
  needsRapim,
  laporanStatus,
}) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  // ✅ FIX 1: showRejectedState HANYA aktif saat status laporan
  // memang sedang dalam kondisi ditolak (bukan setelah submit ulang)
  const isCurrentlyRejected = REJECTED_STATUSES.includes(laporanStatus);
  const isDraftAfterReject = laporanStatus === 'draf' || laporanStatus === 'draft';
  
  // ✅ FIX 2: Cek apakah laporan sudah diajukan ulang
  // Tandai: jika ada step rejected DAN laporan sudah diajukan ulang
  const hasRejectedStepInHistory = steps.some((s) => s.status === 'rejected');
  const isResubmitted = hasRejectedStepInHistory && ACTIVE_PROCESS_STATUSES.includes(laporanStatus);

  // showRejectedState HANYA true saat memang sedang ditolak
  // BUKAN saat sudah diajukan ulang
  const showRejectedState = (isCurrentlyRejected || isDraftAfterReject) && hasRejectedStepInHistory && !isResubmitted;

  // ✅ FIX 3: computedStatus TIDAK mengubah 'rejected' menjadi 'pending'
  // Jika laporan sudah diajukan ulang (isResubmitted), 
  // step dengan status lama (rejected) ditampilkan sebagai 'historical'
  // bukan diubah jadi 'pending' yang menyebabkan confusion
  const computedSteps = steps.map((s) => {
    if (isResubmitted) {
      // Setelah submit ulang: rejected lama → tampil sebagai historis
      if (s.status === 'rejected') {
        return { ...s, computedStatus: 'historical_rejected' };
      }
      // Skipped lama setelah rejected → clear
      if (s.status === 'skipped') {
        return { ...s, computedStatus: 'historical_skipped' };
      }
    }
    
    if (showRejectedState) {
      // Saat memang sedang ditolak:
      // step pending yang bukan current → skipped
      if (currentStep === null && s.status === 'pending') {
        return { ...s, computedStatus: 'skipped' };
      }
    }

    return { ...s, computedStatus: s.status };
  });

  // ✅ FIX 4: effectiveCurrentStep LANGSUNG dari backend
  // Tidak ada fallback yang "menebak" dari computedSteps
  // karena itu sumber inkonsistensi utama
  const effectiveCurrentStep = currentStep;

  // Build display steps
  const displaySteps = [
    { key: 'submit', label: 'Diajukan', status: 'done' as const },
    ...computedSteps.map((s) => ({
      key: `step_${s.step_order}`,
      label: s.step_label || `Step ${s.step_order}`,
      status: s.computedStatus,
      stepOrder: s.step_order,
      validator: s.validator?.name,
      canResolve: s.can_resolve_here,
      feedback: s.feedback,
    })),
    ...(resolution && !needsRapim
      ? [{ key: 'resolved', label: `Selesai (${formatResolution(resolution)})`, status: 'final' as const }]
      : needsRapim
        ? [{ key: 'rapim', label: 'RAPIM', status: getPostValidationStatus(laporanStatus) }]
        : [{ key: 'tujuan', label: 'Menunggu', status: 'pending' as const }]
    ),
  ];

  return (
    <div className="w-full">
      {/* ✅ FIX 5: Tambah info banner saat laporan diajukan ulang */}
      {isResubmitted && hasRejectedStepInHistory && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700 font-medium">
            ⚠ Laporan ini pernah ditolak dan telah diajukan ulang. Proses validasi dimulai dari awal.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between relative">
        {/* Background line */}
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-neutral-border"
          style={{ top: '16px' }}
        />

        {displaySteps.map((step, i) => {
          const isDone = step.status === 'done' || step.status === 'approved' || step.status === 'resolved';
          
          // ✅ FIX 6: isCurrent hanya dari effectiveCurrentStep yang dari backend
          const isCurrent = step.status === 'pending'
            && 'stepOrder' in step
            && effectiveCurrentStep !== null
            && effectiveCurrentStep === (step as any).stepOrder;
          
          const isRejected = step.status === 'rejected' && !isResubmitted;
          const isSkipped = step.status === 'skipped';
          const isResolved = step.status === 'resolved';
          const isFinal = step.status === 'final';
          
          // ✅ FIX 7: Historis (setelah submit ulang) tampil berbeda
          const isHistoricalRejected = step.status === 'historical_rejected';
          const isHistoricalSkipped = step.status === 'historical_skipped';

          return (
             <div
              key={step.key}
              className="relative flex flex-col items-center z-10"
              style={{ width: `${100 / displaySteps.length}%` }}
            >
              {/* Connector line */}
              {i > 0 && (
                <div
                  className={`absolute top-4 -translate-y-1/2 h-0.5 right-1/2 ${
                    isDone || isFinal ? 'bg-teal-main' : 
                    isHistoricalRejected ? 'bg-amber-300' : 
                    'bg-neutral-border'
                  }`}
                  style={{ width: '100%', left: '-50%' }}
                />
              )}

              {/* Circle */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all
                  ${isDone ? 'bg-teal-main border-teal-main text-white' : ''}
                  ${isResolved ? 'bg-emerald-500 border-emerald-500 text-white' : ''}
                  ${isCurrent ? 'bg-white border-teal-main text-teal-main ring-4 ring-teal-main/20' : ''}
                  ${isRejected ? 'bg-danger border-danger text-white ring-4 ring-danger/20' : ''}
                  ${isSkipped ? 'bg-gray-200 border-gray-300 text-gray-400' : ''}
                  ${isFinal ? 'bg-success border-success text-white' : ''}
                  ${isHistoricalRejected ? 'bg-amber-100 border-amber-300 text-amber-600' : ''}
                  ${isHistoricalSkipped ? 'bg-gray-100 border-gray-200 text-gray-300' : ''}
                  ${!isDone && !isCurrent && !isRejected && !isSkipped && !isResolved && !isFinal && !isHistoricalRejected && !isHistoricalSkipped
                    ? 'bg-white border-neutral-border text-neutral-text-secondary' : ''}
                `}
              >
                {isDone && <Check className="w-4 h-4" />}
                {isResolved && <CheckCircle className="w-4 h-4" />}
                {isCurrent && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isRejected && <XCircle className="w-4 h-4" />}
                {isSkipped && <span className="text-[9px]">—</span>}
                {isFinal && <Check className="w-4 h-4" />}
                {isHistoricalRejected && <XCircle className="w-3 h-3" />}
                {isHistoricalSkipped && <span className="text-[9px]">—</span>}
                {!isDone && !isCurrent && !isRejected && !isSkipped && !isResolved && !isFinal && !isHistoricalRejected && !isHistoricalSkipped && (
                  <span>{i + 1}</span>
                )}
              </div>

              {/* Label */}
              <span
                className={`mt-2 text-[10px] font-medium text-center leading-tight max-w-[80px]
                  ${isCurrent ? 'text-teal-main font-bold' : ''}
                  ${isRejected ? 'text-danger font-bold' : ''}
                  ${isDone || isResolved ? 'text-teal-dark' : ''}
                  ${isSkipped ? 'text-gray-400 line-through' : ''}
                  ${isFinal ? 'text-success font-bold' : ''}
                  ${isHistoricalRejected ? 'text-amber-500 text-[9px]' : ''}
                  ${isHistoricalSkipped ? 'text-gray-300 text-[9px] line-through' : ''}
                  ${!isDone && !isCurrent && !isRejected && !isSkipped && !isResolved && !isFinal && !isHistoricalRejected && !isHistoricalSkipped
                    ? 'text-neutral-text-secondary' : ''}
                `}
              >
                {step.label}
              </span>

              {/* Validator name */}
              {'validator' in step && step.validator && !isHistoricalRejected && !isHistoricalSkipped && (
                <span className="mt-0.5 text-[9px] text-neutral-text-secondary truncate max-w-[70px]">
                  {step.validator}
                </span>
              )}

              {/* Historical rejected badge */}
              {isHistoricalRejected && (
                 <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-amber-50 text-amber-500 border border-amber-200">
                  Pernah ditolak
                </span>
              )}

              {/* Resolved badge */}
              {isResolved && (
                 <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-emerald-100 text-emerald-700">
                  Diselesaikan
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ✅ FIX 8: Rejected indicator HANYA saat memang sedang ditolak */}
      {showRejectedState && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-danger/10 text-danger">
            ⚠ Ditolak — perlu revisi pelapor
          </span>
        </div>
      )}

      {/* Resolved indicator */}
      {resolution && !needsRapim && (
        <div className="mt-3 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            ✓ Diselesaikan di tingkat {formatResolution(resolution)}
          </span>
        </div>
      )}
    </div>
  );
};

function formatResolution(level: string): string {
  const labels: Record<string, string> = {
    fakultas: 'Fakultas',
    lembaga: 'Lembaga',
    warek: 'Wakil Rektor',
    universitas: 'Rapat Pimpinan',
  };
  return labels[level] || level;
}

function getPostValidationStatus(laporanStatus: string): 'done' | 'pending' {
  const postStatuses = ['antrean_rapim', 'diputuskan', 'berlangsung', 'selesai', 'diarsipkan'];
  return postStatuses.includes(laporanStatus) ? 'done' : 'pending';
}

export default WorkflowStatusProgress;
